// src/components/Header.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import styles from '../styles/Header.module.css';
import { fetchUserProfile } from '../store/userSlice';
import UserMenu from './UserMenu';
// 导入 i18n 相关
import { useTranslation } from 'react-i18next';

// 语言选项配置
const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'zh', name: '简体中文' },
  { code: 'hk', name: '繁體中文' }
];

const Header = ({ onLoginClick, onStaffLoginClick, minimal }) => {
  const { isLoggedIn, user } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const location = useLocation();
  // 添加 i18n hooks
  const { t, i18n } = useTranslation();

  // 状态管理
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isUserLoading, setIsUserLoading] = useState(true);

  const languageDropdownRef = useRef(null);

  // 获取当前语言，考虑可能的区域代码 (如 'en-US' 仍应映射到 'en')
  const getCurrentLanguage = useCallback(() => {
    const fullLang = i18n.language || 'en';
    // 处理可能包含区域的语言代码 (如 'zh-CN')
    const baseLang = fullLang.split('-')[0];
    
    // 特殊处理：如果是 'zh-HK' 或 'zh-TW'，返回 'hk'
    if (fullLang.includes('HK') || fullLang.includes('TW')) {
      return 'hk';
    }
    
    // 检查是否在支持的语言列表中
    const isSupported = LANGUAGES.some(lang => lang.code === baseLang);
    return isSupported ? baseLang : 'en';
  }, [i18n.language]);

  // 获取当前语言对象
  const currentLanguage = LANGUAGES.find((lang) => lang.code === getCurrentLanguage()) || LANGUAGES[0];

  // 语言切换处理函数
  const handleLanguageChange = useCallback((code) => {
    console.log('Changing language to:', code);
    i18n.changeLanguage(code);
    setIsLanguageOpen(false);
  }, [i18n]);

  // 检查用户数据是否加载完成
  useEffect(() => {
    const loadUserData = async () => {
      setIsUserLoading(true);
      const uid = localStorage.getItem('userId');
      if (uid) {
        try {
          const result = await dispatch(fetchUserProfile()).unwrap();
          console.log('fetchUserProfile result:', result);
        } catch (err) {
          console.error('Failed to fetch user profile:', err);
          localStorage.removeItem('userId');
          localStorage.removeItem('uid');
        }
      } else {
        console.log('No uid found in localStorage, skipping fetch');
      }
      setIsUserLoading(false);
    };

    loadUserData();
  }, [dispatch]);

  // 点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isLanguageOpen &&
        languageDropdownRef.current &&
        !languageDropdownRef.current.contains(event.target)
      ) {
        setIsLanguageOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isLanguageOpen]);

  const toggleLanguageDropdown = useCallback(() => {
    setIsLanguageOpen((prev) => !prev);
  }, []);

  return (
    <header className={styles.header}>
      <div className={styles.container}>
        <nav className={styles.nav}>
          {/* Logo */}
          <Link to="/" className={styles.logo}>
            <img src={`${process.env.PUBLIC_URL}/images/logo.jpg`} alt="Logo" className={styles.logoImage} />
            {!minimal && <span className={styles.logoText}>{t('header.logo')}</span>}
          </Link>

          {/* 右侧登录按钮（minimal模式也显示） */}
          <div className={styles.rightSection}>
            {isUserLoading ? (
              <div className={styles.loading}>
                <i className="fas fa-spinner fa-spin"></i>
              </div>
            ) : isLoggedIn ? (
              <UserMenu />
            ) : (
              <div className={styles.authButtons}>
                <a href="#" className={`${styles.btn} ${styles.login}`} onClick={onStaffLoginClick}>
                  客服专用
                </a>
                <a href="#" className={`${styles.btn} ${styles.signup}`} onClick={onLoginClick}>
                  手机号账号 获取默认密码
                </a>
              </div>
            )}
          </div>

          {/* 导航栏 + 语言（非minimal才显示） */}
          {!minimal && (
            <>
              <div className={styles.navLinks}>
                <Link
                  to="/fleet"
                  className={`${styles.navLink} ${location.pathname === '/fleet' ? styles.active : ''}`}
                >
                  {t('header.aiFleet')}
                </Link>
                <Link
                  to="/account"
                  className={`${styles.navLink} ${styles.hideOnMobile} ${
                    location.pathname === '/account' ? styles.active : ''
                  }`}
                >
                  {t('header.customerService')}
                </Link>
              </div>
              <div className={styles.langSelector} ref={languageDropdownRef}>
                <button
                  className={styles.langButton}
                  onClick={toggleLanguageDropdown}
                  aria-expanded={isLanguageOpen}
                  aria-label={t('header.selectLanguage')}
                >
                  <i className={`fas fa-globe ${styles.langIcon}`}></i>
                  <span>{currentLanguage.name}</span>
                  <i
                    className={`fas fa-chevron-down ${styles.langArrow} ${
                      isLanguageOpen ? styles.langArrowRotate : ''
                    }`}
                  ></i>
                </button>
                {isLanguageOpen && (
                  <div className={styles.langDropdown} role="menu">
                    {LANGUAGES.map((lang) => (
                      <div
                        key={lang.code}
                        className={`${styles.langOption} ${
                          getCurrentLanguage() === lang.code ? styles.langOptionActive : ''
                        }`}
                        onClick={() => handleLanguageChange(lang.code)}
                        role="menuitem"
                        tabIndex={0}
                      >
                        {lang.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </nav>
      </div>
    </header>
  );
};

export default React.memo(Header);